import { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Play, X, Volume2, VolumeX } from "lucide-react";
import { REEL } from "../data/content";
import { Rise } from "./motion/Reveal";

const Tile = ({ item, index, onOpen, soundOn, onToggleSound }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !soundOn;
    if (soundOn) { v.volume = 1; v.play().catch(() => {}); }
  }, [soundOn]);

  return (
    <motion.div
      data-testid={`reel-tile-${item.id}`}
      whileHover="hover"
      onClick={() => onOpen(item)}
      className={`group relative min-h-[260px] cursor-pointer overflow-hidden rounded-2xl border transition-colors md:min-h-[360px] ${item.span} ${soundOn ? "border-electric/70" : "border-white/10"}`}
    >
      <motion.video
        ref={videoRef}
        src={item.src}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        variants={{ hover: { scale: 1.06 } }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 h-full w-full object-cover brightness-[0.8]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-transparent" />
      <motion.div
        variants={{ hover: { opacity: 1 } }}
        initial={{ opacity: 0 }}
        className="absolute inset-0 bg-gradient-to-tr from-magenta/25 via-transparent to-electric/20"
      />
      <div className={`absolute inset-0 rounded-2xl ring-1 ring-inset transition-all duration-500 ${soundOn ? "ring-electric/70 shadow-[0_0_50px_-5px_rgba(0,240,255,0.4)]" : "ring-transparent group-hover:ring-electric/60 group-hover:shadow-[0_0_50px_-5px_rgba(0,240,255,0.35)]"}`} />

      {/* Sound toggle, does not open lightbox */}
      <button
        data-testid={`reel-sound-${item.id}`}
        onClick={(e) => { e.stopPropagation(); onToggleSound(item.id); }}
        aria-label={soundOn ? "Mute" : "Play sound"}
        className={`absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full transition-colors ${soundOn ? "bg-electric text-ink" : "glass text-cream hover:border-electric/50"}`}
      >
        {soundOn ? <Volume2 size={17} /> : <VolumeX size={17} />}
        {soundOn && (
          <motion.span
            className="absolute inset-0 rounded-full border border-electric"
            animate={{ scale: [1, 1.5], opacity: [0.6, 0] }}
            transition={{ repeat: Infinity, duration: 1.4 }}
          />
        )}
      </button>

      <div className="relative flex h-full flex-col justify-between p-5 text-left">
        <span className="w-fit glass rounded-full px-3 py-1.5 font-mono text-[0.6rem] uppercase tracking-wider text-cream">
          {String(index + 1).padStart(2, "0")} · {item.tag}
        </span>
        <div className="flex items-end justify-between">
          <h3 className="font-heading text-2xl font-bold text-cream md:text-3xl">{item.title}</h3>
          <motion.span
            variants={{ hover: { scale: 1.15 } }}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-cream text-ink"
          >
            <Play size={18} className="ml-0.5" />
          </motion.span>
        </div>
      </div>
    </motion.div>
  );
};

const Lightbox = ({ item, onClose }) => (
  <motion.div
    data-testid="reel-lightbox"
    className="fixed inset-0 z-[1000] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-xl md:p-10"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    onClick={onClose}
  >
    <button
      data-testid="reel-lightbox-close"
      onClick={onClose}
      className="glass fixed right-5 top-5 z-10 flex h-12 w-12 items-center justify-center rounded-full text-cream transition-colors hover:border-magenta/50 hover:text-magenta"
    >
      <X size={20} />
    </button>
    <motion.div
      onClick={(e) => e.stopPropagation()}
      initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="glass w-full max-w-5xl overflow-hidden rounded-2xl p-2 shadow-[0_40px_120px_-30px_rgba(0,240,255,0.35)]"
    >
      <video
        src={item.src}
        autoPlay
        loop
        controls
        playsInline
        className="max-h-[80vh] w-full rounded-xl bg-black object-contain"
      />
      <div className="flex items-center gap-2 px-3 py-3 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-white/50">
        <Volume2 size={14} className="text-electric" /> {item.title} · {item.tag} · full audio
      </div>
    </motion.div>
  </motion.div>
);

export const VideoPanel = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(null);
  const [soundId, setSoundId] = useState(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  const openLightbox = (item) => { setSoundId(null); setActive(item); };
  const toggleSound = (id) => setSoundId((cur) => (cur === id ? null : id));

  return (
    <section ref={ref} data-testid="video-section" className="relative mx-auto max-w-[1400px] px-5 py-20 md:px-12 md:py-32">
      <div className="orb bg-electric" style={{ width: 500, height: 500, top: "10%", left: "20%", opacity: 0.12 }} />

      <Rise className="mb-12 flex items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-electric">(Motion Reel)</p>
          <h2 className="mt-3 font-display text-4xl font-extrabold uppercase tracking-tighter md:text-7xl">In Motion</h2>
        </div>
        <span className="hidden max-w-[240px] text-right font-mono text-[0.62rem] uppercase leading-relaxed tracking-[0.22em] text-white/40 md:block">
          Tap the speaker to hear a clip · click a tile to expand with full sound
        </span>
      </Rise>

      <motion.div style={{ y }} className="grid grid-cols-1 gap-5 md:grid-cols-6">
        {REEL.map((item, i) => (
          <Tile
            key={item.id}
            item={item}
            index={i}
            onOpen={openLightbox}
            soundOn={soundId === item.id}
            onToggleSound={toggleSound}
          />
        ))}
      </motion.div>

      <AnimatePresence>
        {active && <Lightbox item={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
};
